import React, { useState, useEffect } from "react";
import axios from "axios";
import { Users, Plus, Pencil, Trash2, Search, X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const EMPTY = {
  nama: "",
  nik: "",
  no_hp: "",
  alamat: "",
  jabatan: "Teknisi",
  tanggal_bergabung: new Date().toISOString().slice(0, 10),
  status: "aktif",
};

const JABATAN_OPTIONS = ["Owner", "Direktur", "Manager", "Supervisor", "Teknisi Senior", "Teknisi", "Kasir/Finance", "Marketing", "Admin", "CS"];

const KaryawanForm = ({ initial, onClose, onSuccess }) => {
  const [form, setForm] = useState(initial || EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (initial?.id) {
        await axios.put(`${BACKEND_URL}/api/master/karyawan/${initial.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/master/karyawan`, form, { withCredentials: true });
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Gagal menyimpan karyawan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="karyawan-form-modal">
      <div className="w-full max-w-lg rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl">
        <div className="p-5 border-b border-slate-800 flex justify-between items-center">
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <Users className="w-4 h-4 text-emerald-400" />
            {initial?.id ? "Edit Karyawan" : "Tambah Karyawan"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          {error && <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Nama Lengkap</label>
            <input required type="text" value={form.nama} onChange={(e) => setField("nama", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-emerald-500" data-testid="karyawan-nama-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">NIK</label>
              <input required type="text" value={form.nik} onChange={(e) => setField("nik", e.target.value)} placeholder="16 digit" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="karyawan-nik-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">No HP</label>
              <input required type="text" value={form.no_hp} onChange={(e) => setField("no_hp", e.target.value)} placeholder="0812xxxx" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="karyawan-hp-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Alamat</label>
            <textarea required rows={2} value={form.alamat} onChange={(e) => setField("alamat", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="karyawan-alamat-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Jabatan</label>
              <select value={form.jabatan} onChange={(e) => setField("jabatan", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="karyawan-jabatan-select">
                {JABATAN_OPTIONS.map((j) => <option key={j} value={j}>{j}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Tanggal Bergabung</label>
              <input required type="date" value={form.tanggal_bergabung} onChange={(e) => setField("tanggal_bergabung", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="karyawan-tanggal-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Status</label>
            <select value={form.status} onChange={(e) => setField("status", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="karyawan-status-select">
              <option value="aktif">Aktif</option>
              <option value="cuti">Cuti</option>
              <option value="resign">Resign</option>
            </select>
          </div>
          <div className="flex gap-2 pt-2 border-t border-slate-800">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700">Batal</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="karyawan-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {initial?.id ? "Update" : "Simpan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const MasterKaryawanPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/master/karyawan`, { withCredentials: true });
      setItems(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, nama) => {
    if (!window.confirm(`Hapus karyawan "${nama}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/master/karyawan/${id}`, { withCredentials: true });
      load();
    } catch (err) { alert(err.response?.data?.detail || "Gagal hapus"); }
  };

  const filtered = items.filter(
    (k) => !search || k.nama?.toLowerCase().includes(search.toLowerCase()) || k.nik?.includes(search) || k.jabatan?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="karyawan-page">
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex flex-col md:flex-row justify-between md:items-center gap-3">
        <div>
          <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase">Master Data</span>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">Data Karyawan</h1>
          <p className="text-xs text-slate-400 mt-0.5">Kelola tim operasional ISP: teknisi, kasir, marketing, dsb.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-bold rounded-xl shadow-lg shadow-emerald-600/30 flex items-center gap-2" data-testid="add-karyawan-btn">
          <Plus className="w-4 h-4" /> Tambah Karyawan
        </button>
      </div>

      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama/NIK/jabatan..." className="w-full pl-9 pr-3 py-1.5 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" />
          </div>
          <span className="text-xs text-slate-500">Total: <strong className="text-emerald-400">{filtered.length}</strong></span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs" data-testid="karyawan-table">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400">
                <th className="py-2.5 px-3">Nama</th>
                <th className="py-2.5 px-3">NIK</th>
                <th className="py-2.5 px-3">No HP</th>
                <th className="py-2.5 px-3">Jabatan</th>
                <th className="py-2.5 px-3">Tgl Bergabung</th>
                <th className="py-2.5 px-3 text-center">Status</th>
                <th className="py-2.5 px-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {loading ? (
                <tr><td colSpan={7} className="py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Memuat...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="py-6 text-center text-slate-500 italic">Belum ada karyawan</td></tr>
              ) : filtered.map((k) => (
                <tr key={k.id} className="hover:bg-slate-900/40" data-testid={`karyawan-row-${k.id}`}>
                  <td className="py-2.5 px-3 font-semibold text-white">{k.nama}<div className="text-[10px] text-slate-500 font-normal">{k.alamat}</div></td>
                  <td className="py-2.5 px-3 font-mono text-slate-300">{k.nik}</td>
                  <td className="py-2.5 px-3 font-mono text-slate-300">{k.no_hp}</td>
                  <td className="py-2.5 px-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-cyan-300 text-[10px] font-bold">{k.jabatan}</span></td>
                  <td className="py-2.5 px-3 font-mono text-slate-400">{k.tanggal_bergabung}</td>
                  <td className="py-2.5 px-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      k.status === "aktif" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" :
                      k.status === "cuti" ? "bg-amber-500/20 text-amber-300 border border-amber-500/30" :
                      "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                    }`}>{k.status?.toUpperCase()}</span>
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => { setEditing(k); setShowForm(true); }} className="p-1 rounded bg-slate-800 hover:bg-amber-900/60 text-amber-400" data-testid={`edit-karyawan-${k.id}`}><Pencil className="w-3 h-3" /></button>
                      <button onClick={() => handleDelete(k.id, k.nama)} className="p-1 rounded bg-slate-800 hover:bg-rose-900/60 text-rose-400" data-testid={`delete-karyawan-${k.id}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <KaryawanForm initial={editing} onClose={() => setShowForm(false)} onSuccess={load} />}
    </div>
  );
};
